import { useCallback, useEffect, useState } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import { getLogList, getLogs } from '../../services/LogsService';
import './LogsPage.css';

const LOG_LABELS = {
    'orca-liquidity.log': 'Pool — saque / encerrar / taxas',
    'orca.log': 'Pool — geral',
    'orca-deposit.log': 'Pool — depósito'
};

function logDisplayName(filename) {
    return LOG_LABELS[filename] || filename;
}

function LogsPage() {
    const [files, setFiles] = useState([]);
    const [total, setTotal] = useState(0);
    const [selected, setSelected] = useState('');
    const [content, setContent] = useState('');
    const [loadingList, setLoadingList] = useState(true);
    const [loadingFile, setLoadingFile] = useState(false);
    const [error, setError] = useState('');

    const [refreshNonce, setRefreshNonce] = useState(0);

    const loadList = useCallback(async () => {
        setLoadingList(true);
        setError('');
        try {
            const data = await getLogList({ page: 1, pageSize: 500 });
            const rows = data.rows || [];
            setFiles(rows);
            setTotal(data.count ?? rows.length);
            setSelected(current => {
                if (current && rows.includes(current)) return current;
                return rows[0] || '';
            });
        } catch (err) {
            const msg = err?.response?.data;
            setError(typeof msg === 'string' ? msg : err?.message || 'Erro ao listar logs.');
            setFiles([]);
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        loadList();
    }, [loadList]);

    useEffect(() => {
        if (!selected) {
            setContent('');
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoadingFile(true);
            setError('');
            try {
                const text = await getLogs(selected.replace(/\.log$/i, ''));
                if (!cancelled) setContent(typeof text === 'string' ? text : String(text ?? ''));
            } catch (err) {
                if (!cancelled) {
                    const msg = err?.response?.data;
                    setContent('');
                    setError(typeof msg === 'string' ? msg : err?.message || 'Erro ao ler o ficheiro.');
                }
            } finally {
                if (!cancelled) setLoadingFile(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selected, refreshNonce]);

    return (
        <>
            <Menu />
            <main className="content">
                <div className="container-fluid py-4 logs-page">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                        <div>
                            <h1 className="h3 mb-0">Logs do servidor</h1>
                            <p className="text-muted small mb-0">
                                Conteúdo da pasta <code>backend/logs</code> permitido para a sua sessão
                                {total ? ` (${total} ficheiro${total === 1 ? '' : 's'})` : ''}.
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={loadList} disabled={loadingList}>
                                {loadingList ? 'A atualizar…' : 'Atualizar lista'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => selected && setRefreshNonce(n => n + 1)}
                                disabled={!selected || loadingFile}
                            >
                                {loadingFile ? 'A carregar…' : 'Recarregar ficheiro'}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="row g-3">
                        <div className="col-lg-3">
                            <label className="form-label" htmlFor="logs-file-select">Ficheiro</label>
                            <select
                                id="logs-file-select"
                                className="form-select"
                                value={selected}
                                onChange={e => setSelected(e.target.value)}
                                disabled={loadingList || !files.length}
                            >
                                {!files.length && <option value="">— Nenhum log disponível —</option>}
                                {files.map(name => (
                                    <option key={name} value={name}>{logDisplayName(name)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-lg-9">
                            <label className="form-label">Conteúdo</label>
                            <pre className="logs-page-pre" aria-live="polite">
                                {loadingFile ? 'A carregar…' : (content || '(vazio)')}
                            </pre>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}

export default LogsPage;
